import { Actions, ofType } from '@ngrx/effects';
import { AnchorNodeData, AnchorTypeNodeData } from './tree.model';
import { BaseAnchor, BaseVersion } from 'src/models/anchor-and-version/base.model';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    inject,
} from '@angular/core';
import {
    ConfigState,
    selectIsOriginWorkspace,
    selectIsReadOnlyUser,
} from 'src/store/reducers/config.reducer';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import { Subscription, distinctUntilChanged, filter, firstValueFrom } from 'rxjs';
import { addInstanceToTree, refreshDynamicDiagram } from 'src/store/actions/tree-navigation.action';
import {
    addNewAnchorsFirst,
    buildAnchorNodesList,
    createLoadMoreNode,
    findNodeByKey,
    getNodeKey,
    getNodePath,
    highlightAnchorNode,
    isNewlyCreatedAnchor,
    removeHighlightFromAnchor,
    renderAnchorNodes,
    updateNode,
} from './utils/tree.util';
import { AllocationElementDiagramDuplicateService } from '../diagrams/utils/allocation-element-diagram-duplicate.service';
import { AnchorAndVersionBaseFilter } from 'src/filters/anchor-and-version-filter/anchor-and-version-base.filter';
import { ChangeRequestDetailsComponent } from '../change-request-details/change-request-details.component';
import { CommonModule } from '@angular/common';
import { NodeType } from './enums/node-type.enum';
import { Store } from '@ngrx/store';
import { TreeType } from './enums/tree-type.enum';
import { WorkspaceDialogComponent } from '../workspace-dialog/workspace-dialog.component';
import { openDuplicateDiagramModal } from '../diagrams/utils/duplicate-diagram-modal.util';
import { updateSidebarVisible } from 'src/store/actions/config.action';

// PrimeNG
// eslint-disable-next-line sort-imports
import {
    ConfirmationService,
    type MenuItem,
    type MenuItemCommandEvent,
    type TreeNode,
} from 'primeng/api';
import { AccordionModule } from 'primeng/accordion';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { TieredMenu } from 'primeng/tieredmenu';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { TreeModule } from 'primeng/tree';
import { TreeNodeExpandEvent } from 'primeng/tree';
import { TreeTableModule } from 'primeng/treetable';

// Utils
// eslint-disable-next-line sort-imports
import { createAnchorTypeNodes, getTreeTypeFromDiagramLabel } from './utils/anchor-type-node.util';
import { createTreeModalMap, createTreeServiceMap } from './utils/tree-map.util';
import { HighlightTextComponent } from '../utils/highlight-text/highlight-text.component';
import { SearchLazyComponent } from '../utils/search-lazy/search-lazy.component';
import { getMenuItems } from './utils/menu-items.util';
import { refreshPendingChangesCount } from 'src/utils/pending-changes-count.util';

// Modals
// eslint-disable-next-line sort-imports
import { CrudAddressModalComponent } from '../crud/crud-modals/crud-address-modal.component';
import { CrudAllocationElementDiagramModalComponent } from '../crud/crud-modals/crud-allocation-element-diagram-modal.component';
import { CrudAllocationElementModalComponent } from '../crud/crud-modals/crud-allocation-element-modal.component';
import { CrudComponentCodeModalComponent } from '../crud/crud-modals/crud-component-code-modal.component';
import { CrudComponentModalComponent } from '../crud/crud-modals/crud-component-modal.component';
import { CrudFunctionCategoryModalComponent } from '../crud/crud-modals/crud-function-category-modal.component';
import { CrudMessageModalComponent } from '../crud/crud-modals/crud-message-modal.component';
import { CrudScenarioModalComponent } from '../crud/crud-modals/crud-scenario-modal.component';
import { CrudSegmentModalComponent } from '../crud/crud-modals/crud-segment-modal.component';
import { CrudSignalModalComponent } from '../crud/crud-modals/crud-signal-modal.component';
import { CrudUseCaseModalComponent } from '../crud/crud-modals/crud-use-case-modal.component';
import { CrudUserFunctionModalComponent } from '../crud/crud-modals/crud-user-function-modal.component';

// Services
// eslint-disable-next-line sort-imports
import { AddressService } from 'src/services/anchor-and-version-services/address.service';
import { AllocationElementDiagramService } from 'src/services/anchor-and-version-services/allocation-element-diagram.service';
import { AllocationElementService } from 'src/services/anchor-and-version-services/allocation-element.service';
import { CanPortDataService } from 'src/services/anchor-and-version-services/can-port-data.service';
import { ChangeRequestService } from 'src/services/basic-services/change-request.service';
import { ComponentCodeService } from 'src/services/anchor-and-version-services/component-code.service';
import { ComponentService } from 'src/services/anchor-and-version-services/component.service';
import { DirectWirePortDataService } from 'src/services/anchor-and-version-services/direct-wire-port-data.service';
import { FunctionCategoryService } from 'src/services/anchor-and-version-services/function-category.service';
import { InternalPortDataService } from 'src/services/anchor-and-version-services/internal-port-data.service';
import { MessageService as MessageEntityService } from 'src/services/anchor-and-version-services/message.service';
import { NotificationService } from 'src/services/notification.service';
import { PendingChangesService } from 'src/services/pending-changes-service/pending-changes.service';
import { ScenarioService } from 'src/services/anchor-and-version-services/scenario.service';
import { SegmentService } from 'src/services/anchor-and-version-services/segment.service';
import { SignalService } from 'src/services/anchor-and-version-services/signal.service';
import { TreeService } from 'src/services/tree-service/tree.service';
import { UseCaseService } from 'src/services/anchor-and-version-services/use-case.service';
import { UserFunctionService } from 'src/services/anchor-and-version-services/user-function.service';

@Component({
    selector: 'app-tree',
    templateUrl: './tree.component.html',
    styleUrls: ['./tree.component.scss'],
    imports: [
        ChangeRequestDetailsComponent,
        CommonModule,
        FormsModule,
        HighlightTextComponent,
        ReactiveFormsModule,
        SearchLazyComponent,
        WorkspaceDialogComponent,
        // Modal Components
        CrudAddressModalComponent,
        CrudAllocationElementDiagramModalComponent,
        CrudAllocationElementModalComponent,
        CrudComponentCodeModalComponent,
        CrudComponentModalComponent,
        CrudFunctionCategoryModalComponent,
        CrudMessageModalComponent,
        CrudScenarioModalComponent,
        CrudSegmentModalComponent,
        CrudSignalModalComponent,
        CrudUseCaseModalComponent,
        CrudUserFunctionModalComponent,
        // PrimeNG
        AccordionModule,
        BadgeModule,
        ButtonModule,
        DialogModule,
        InputGroupModule,
        InputTextModule,
        ProgressBarModule,
        TieredMenuModule,
        ToggleSwitchModule,
        TooltipModule,
        TreeModule,
        TreeTableModule,
    ],
})
export class TreeComponent implements OnInit, OnDestroy {
    @Output() public instanceSelected = new EventEmitter<{
        anchorKey: number;
        objectType: string;
    }>();

    // Modals
    // eslint-disable-next-line @typescript-eslint/member-ordering
    @ViewChild('addressModal') public addressModal!: CrudAddressModalComponent;
    @ViewChild('allocationElementDiagramModal')
    public allocationElementDiagramModal!: CrudAllocationElementDiagramModalComponent;
    @ViewChild('allocationElementModal')
    public allocationElementModal!: CrudAllocationElementModalComponent;
    @ViewChild('changeRequestDetailsModal')
    public changeRequestDetailsModal!: ChangeRequestDetailsComponent;
    @ViewChild('componentCodeModal') public componentCodeModal!: CrudComponentCodeModalComponent;
    @ViewChild('componentModal') public componentModal!: CrudComponentModalComponent;
    @ViewChild('functionCategoryModal')
    public functionCategoryModal!: CrudFunctionCategoryModalComponent;
    @ViewChild('messageModal') public messageModal!: CrudMessageModalComponent;
    @ViewChild('scenarioModal') public scenarioModal!: CrudScenarioModalComponent;
    @ViewChild('segmentModal') public segmentModal!: CrudSegmentModalComponent;
    @ViewChild('signalModal') public signalModal!: CrudSignalModalComponent;
    @ViewChild('menu') public tieredMenu!: TieredMenu;
    @ViewChild('useCaseModal') public useCaseModal!: CrudUseCaseModalComponent;
    @ViewChild('userFunctionModal') public userFunctionModal!: CrudUserFunctionModalComponent;
    @ViewChild('workspaceDialog') public workspaceDialog!: WorkspaceDialogComponent;

    public TreeType = TreeType;
    public currentAnchorKey?: number;
    public currentTreeType?: TreeType;

    public isSearching = false;
    public isVisibleWorkspaceDialog = false;
    public menuItems: MenuItem[] | undefined;
    public nodes: TreeNode<AnchorNodeData | AnchorTypeNodeData>[] = [];
    public pendingDuplicateAedNode?: TreeNode<AnchorNodeData>;
    public pendingModalNode?: TreeNode<AnchorNodeData>;
    public searchString = '';
    public showSearch = false;
    private hasMoreMap = new Map<string, boolean>();
    private isReadOnly = false;
    private loadedCounts = new Map<string, number>();
    private menuTarget?: HTMLElement;

    // Subscriptions
    // eslint-disable-next-line @typescript-eslint/member-ordering
    private configSub!: Subscription;
    private roleSub!: Subscription;
    private routerSub!: Subscription;
    private treeSub!: Subscription;
    private workspaceSub!: Subscription;

    // Inject
    // eslint-disable-next-line @typescript-eslint/member-ordering
    private actions$ = inject(Actions);
    private addressService = inject(AddressService);
    private allocationElementDiagramDuplicateService = inject(
        AllocationElementDiagramDuplicateService
    );
    private allocationElementDiagramService = inject(AllocationElementDiagramService);
    private allocationElementService = inject(AllocationElementService);
    private canPortDataService = inject(CanPortDataService);
    private cdr = inject(ChangeDetectorRef);
    private componentCodeService = inject(ComponentCodeService);
    private componentService = inject(ComponentService);
    private confirmationService = inject(ConfirmationService);
    private crService = inject(ChangeRequestService);
    private directWirePortDataService = inject(DirectWirePortDataService);
    private elementRef = inject(ElementRef<HTMLElement>);
    private functionCategoryService = inject(FunctionCategoryService);
    private internalPortDataService = inject(InternalPortDataService);
    private messageEntityService = inject(MessageEntityService);
    private notificationService = inject(NotificationService);
    private pendingChangesService = inject(PendingChangesService);
    private router = inject(Router);
    private scenarioService = inject(ScenarioService);
    private segmentService = inject(SegmentService);
    private signalService = inject(SignalService);
    private store = inject<Store<{ config: ConfigState }>>(Store);
    private treeService = inject(TreeService);
    private useCaseService = inject(UseCaseService);
    private userFunctionService = inject(UserFunctionService);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private modalMap = createTreeModalMap(this);

    private serviceMap = createTreeServiceMap({
        addressService: this.addressService,
        allocationElementDiagramService: this.allocationElementDiagramService,
        allocationElementService: this.allocationElementService,
        canPortDataService: this.canPortDataService,
        componentCodeService: this.componentCodeService,
        componentService: this.componentService,
        directWirePortDataService: this.directWirePortDataService,
        functionCategoryService: this.functionCategoryService,
        internalPortDataService: this.internalPortDataService,
        messageEntityService: this.messageEntityService,
        scenarioService: this.scenarioService,
        segmentService: this.segmentService,
        signalService: this.signalService,
        useCaseService: this.useCaseService,
        userFunctionService: this.userFunctionService,
    });

    private readonly AED_DUPLICATE_TOAST_KEY = 'aed-duplicate';

    /* eslint-disable @typescript-eslint/member-ordering */
    public isOriginWorkspace$ = this.store.select(selectIsOriginWorkspace);
    public isReadOnlyUser$ = this.store.select(selectIsReadOnlyUser);
    /* eslint-enable @typescript-eslint/member-ordering */

    public ngOnDestroy(): void {
        this.workspaceSub?.unsubscribe();
        this.routerSub?.unsubscribe();
        this.treeSub?.unsubscribe();
        this.roleSub?.unsubscribe();
        this.configSub?.unsubscribe();
    }

    public ngOnInit(): void {
        this.nodes = createAnchorTypeNodes(0);
        this.workspaceSub = this.isOriginWorkspace$.subscribe((isOriginWorkspace) => {
            if (!isOriginWorkspace && this.pendingDuplicateAedNode) {
                const node = this.pendingDuplicateAedNode;
                this.pendingDuplicateAedNode = undefined;
                this.duplicateAllocationElementDiagram(node);
            }
            if (!isOriginWorkspace && this.pendingModalNode) {
                this.openModal(this.pendingModalNode);
                this.pendingModalNode = undefined;
            }
        });

        this.routerSub = this.router.events
            .pipe(filter((event) => event instanceof NavigationStart))
            .subscribe(() => {
                Object.values(this.modalMap).forEach((getModal) => {
                    const modal = getModal();
                    modal?.onClose?.();
                });
            });

        this.roleSub = this.isReadOnlyUser$.subscribe((isReadOnly) => {
            this.isReadOnly = isReadOnly;
        });

        this.treeSub = this.store
            .select((state) => state.config.refreshTreeFlag)
            .subscribe(() => {
                this.refreshExpandedAnchors();
            });

        this.configSub = this.store
            .select((state) => ({
                sopDate: state.config.sopDate,
                changeRequestKey: state.config.changeRequest?.key,
                workspaceKey: state.config.workspace?.key,
                diagramFilterConfigId: state.config.diagramFilterConfig?.id,
            }))
            .pipe(
                distinctUntilChanged(
                    (a, b) =>
                        a.sopDate === b.sopDate &&
                        a.changeRequestKey === b.changeRequestKey &&
                        a.workspaceKey === b.workspaceKey &&
                        a.diagramFilterConfigId === b.diagramFilterConfigId
                )
            )
            .subscribe(() => {
                this.refreshExpandedAnchors();
            });

        this.actions$.pipe(ofType(addInstanceToTree)).subscribe((action) => {
            this.handleAddInstanceToTree(action.anchorKey, action.objectType);
        });
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public async addAnchorNode(
        anchorTypeNode: TreeNode<AnchorTypeNodeData>,
        newlyCreatedAnchor?: BaseAnchor<BaseVersion>
    ): Promise<void> {
        try {
            const nodePath = getNodePath(anchorTypeNode);
            const nodeKey = getNodeKey(nodePath);

            const _searchString = anchorTypeNode.parent ? undefined : this.searchString;
            const response = await this.treeService.getAnchors(nodePath, _searchString);
            const pendingAnchors = await this.loadPendingAnchors(anchorTypeNode);
            const anchors = addNewAnchorsFirst(response.items, pendingAnchors, newlyCreatedAnchor);

            const anchorNodes = buildAnchorNodesList(
                anchors,
                anchorTypeNode,
                response.hasMore,
                this.currentAnchorKey
            );

            this.loadedCounts.set(nodeKey, response.items.length);
            this.hasMoreMap.set(nodeKey, response.hasMore);

            anchorTypeNode.children = anchorNodes;
            anchorTypeNode.loading = false;
            updateNode(this.nodes, anchorTypeNode, (n) => (this.nodes = [...n]));
        } catch (error) {
            this.notificationService.handleError('Failed to load anchors', error);
        }
    }

    public async addNodeCategories(node: TreeNode): Promise<void> {
        try {
            const nodePath = getNodePath(node);
            const treeCount = await this.treeService.getCount(nodePath);
            const depth = node.data!.depth + 1;
            node.children = createAnchorTypeNodes(depth, treeCount);
            node.loading = false;
            updateNode(this.nodes, node, (n) => (this.nodes = [...n]));
        } catch (error) {
            this.notificationService.handleError('Failed to load categories', error);
        }
    }

    public clearCurrentAnchor(): void {
        if (this.currentAnchorKey !== undefined) {
            const live = findNodeByKey(this.nodes, this.currentAnchorKey);
            if (live) {
                removeHighlightFromAnchor(live);
                updateNode(this.nodes, live, (n) => (this.nodes = [...n]));
            }
            this.currentAnchorKey = undefined;
        }
    }

    public setCurrentAnchor(anchorNode: TreeNode<AnchorNodeData>): void {
        if (this.currentAnchorKey === anchorNode.data?.anchor?.key) return;
        if (this.currentAnchorKey !== undefined) {
            const live = findNodeByKey(this.nodes, this.currentAnchorKey);
            if (live) {
                removeHighlightFromAnchor(live);
                updateNode(this.nodes, live, (n) => (this.nodes = [...n]));
            }
        }
        highlightAnchorNode(anchorNode);
        this.currentAnchorKey = anchorNode.data?.anchor?.key;
        updateNode(this.nodes, anchorNode, (n) => (this.nodes = [...n]));
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public collapseAll() {
        this.nodes = this.nodes.map((node) => ({ ...node, expanded: false, children: undefined }));
        this.cdr.detectChanges();
    }

    public confirmDuplicateAllocationElementDiagram(
        node: TreeNode<AnchorNodeData>,
        event?: MenuItemCommandEvent
    ): void {
        const target =
            this.getAnchorRowTarget(node) ??
            this.menuTarget ??
            this.getEventTargetElement(event?.originalEvent);
        const originalAedName = this.getAllocationElementDiagramName(node);

        this.confirmationService.confirm({
            target,
            message: `Duplicate "${originalAedName}"?<br><span class="text-red-500">Warning: Unsaved editor changes are not included in the duplicate.</span>`,
            header: 'Duplicate Diagram',
            icon: 'pi pi-copy',
            acceptLabel: 'Confirm',
            acceptButtonStyleClass: '',
            rejectLabel: 'Cancel',
            rejectVisible: true,
            rejectButtonStyleClass: 'p-button-text',
            accept: async () => {
                await this.duplicateAllocationElementDiagram(node);
            },
        });
    }

    public async duplicateAllocationElementDiagram(node: TreeNode<AnchorNodeData>): Promise<void> {
        const anchorKey = node.data?.anchor.key;
        if (!anchorKey) return;
        const originalAedName = this.getAllocationElementDiagramName(node);

        try {
            if (await firstValueFrom(this.isOriginWorkspace$)) {
                this.pendingDuplicateAedNode = node;
                await this.workspaceDialog.open();
                return;
            }

            this.notificationService.clearStickyToast(this.AED_DUPLICATE_TOAST_KEY);
            this.notificationService.showStickyInfo(
                originalAedName,
                'Duplicating Diagram',
                this.AED_DUPLICATE_TOAST_KEY
            );

            const sourceAed =
                await this.allocationElementDiagramService.getFullDiagramForStaticDiagramView(
                    anchorKey
                );
            await openDuplicateDiagramModal({
                modal: this.allocationElementDiagramModal,
                sourceAed,
                allocationElementDiagramService: this.allocationElementDiagramService,
                onCancel: () =>
                    this.notificationService.clearStickyToast(this.AED_DUPLICATE_TOAST_KEY),
                onSave: async (duplicatedAed) => {
                    try {
                        await this.allocationElementDiagramDuplicateService.duplicateDiagramContents(
                            sourceAed,
                            duplicatedAed
                        );
                        await refreshPendingChangesCount(this.store, this.pendingChangesService);

                        this.store.dispatch(
                            addInstanceToTree({
                                anchorKey: duplicatedAed.key,
                                objectType: TreeType.AllocationElementDiagram,
                            })
                        );
                        await this.router.navigate(['/static-diagrams', duplicatedAed.key]);
                        this.notificationService.showSuccess('Diagram duplicated');
                    } finally {
                        this.notificationService.clearStickyToast(this.AED_DUPLICATE_TOAST_KEY);
                    }
                },
            });
        } catch (error) {
            this.notificationService.clearStickyToast(this.AED_DUPLICATE_TOAST_KEY);
            await refreshPendingChangesCount(this.store, this.pendingChangesService);
            this.notificationService.handleError('Failed to duplicate diagram', error);
        }
    }

    public hasExpandedNodes(): boolean {
        return this.nodes.some((node) => node.expanded);
    }

    public hasMenuItems(node: TreeNode<AnchorNodeData>): boolean {
        const menuItems = getMenuItems(
            node,
            (node) => this.openModal(node),
            (node, event) => this.openCRModal(node, event),
            (node) => this.openCloneSignalModal(node),
            (node, event) => this.confirmDuplicateAllocationElementDiagram(node, event),
            (node) => this.setCurrentAnchor(node),
            this.isReadOnly
        );
        return menuItems.length > 0;
    }

    public async loadMoreAnchors(loadMoreNode: TreeNode): Promise<void> {
        loadMoreNode.loading = true;
        try {
            const anchorTypeNode = loadMoreNode.parent! as TreeNode<AnchorTypeNodeData>;
            const nodePath = getNodePath(anchorTypeNode);
            const nodeKey = getNodeKey(nodePath);
            const currentCount = this.loadedCounts.get(nodeKey) || 0;

            const searchString = anchorTypeNode.parent ? undefined : this.searchString;
            const response = await this.treeService.getAnchors(
                nodePath,
                searchString,
                currentCount
            );
            const existingAnchorKeys = new Set(
                (anchorTypeNode.children ?? [])
                    .filter((child) => child.type === NodeType.anchor)
                    .map((child) => (child.data as AnchorNodeData).anchor.key)
            );
            const newAnchors = response.items.filter(
                (anchor) => !existingAnchorKeys.has(anchor.key)
            );

            if (response.items.length > 0) {
                const newAnchorNodes = renderAnchorNodes(
                    newAnchors,
                    anchorTypeNode,
                    this.currentAnchorKey
                );
                const loadMoreIndex = anchorTypeNode.children!.findIndex(
                    (child) => child.type === NodeType.loadMore
                );

                anchorTypeNode.children!.splice(loadMoreIndex, 1);
                anchorTypeNode.children!.push(...newAnchorNodes);

                this.loadedCounts.set(nodeKey, currentCount + response.items.length);
                this.hasMoreMap.set(nodeKey, response.hasMore);

                if (response.hasMore) {
                    anchorTypeNode.children!.push(createLoadMoreNode(anchorTypeNode));
                }

                updateNode(this.nodes, anchorTypeNode, (n) => (this.nodes = [...n]));
            }
        } catch (error) {
            this.notificationService.handleError('Failed to load more anchors', error);
        } finally {
            loadMoreNode.loading = false;
        }
    }

    public async onClickTieredMenu(
        event: MouseEvent,
        node: TreeNode<AnchorNodeData>,
        target?: HTMLElement
    ): Promise<void> {
        event.preventDefault();
        event.stopPropagation();

        this.menuTarget =
            target ?? this.getAnchorRowTarget(node) ?? this.getEventTargetElement(event);
        this.menuItems = getMenuItems(
            node,
            (node) => this.openModal(node),
            (node, event) => this.openCRModal(node, event),
            (node) => this.openCloneSignalModal(node),
            (node, event) => this.confirmDuplicateAllocationElementDiagram(node, event),
            (node) => this.setCurrentAnchor(node),
            this.isReadOnly
        );
        if (this.menuItems.length === 0) return;
        this.tieredMenu.toggle(event);

        if (node.type === NodeType.anchor) {
            setTimeout(() => this.setCurrentAnchor(node));
        }
    }

    public onCloseWorkspaceDialog(): void {
        this.isVisibleWorkspaceDialog = false;
        this.workspaceDialog.close();
    }

    public async onNodeExpand(event: TreeNodeExpandEvent) {
        const node = event.node;
        if (!node.children) {
            node.loading = true;

            try {
                if (node.type === NodeType.anchorType) {
                    await this.addAnchorNode(node);
                } else {
                    await this.addNodeCategories(node);
                }
            } catch (error) {
                this.notificationService.handleError('Failed to expand node', error);
            } finally {
                node.loading = false;
            }
        }
    }

    public async onSearchEvent(value: string): Promise<void> {
        this.searchString = value.trim();
        if (!this.searchString) {
            this.nodes = createAnchorTypeNodes(0);
            return;
        }
        try {
            this.isSearching = true;
            const counts = await this.treeService.getCountByDisplayname(value);
            this.nodes = createAnchorTypeNodes(0, counts).filter((n) => !n.leaf);
        } catch (error) {
            this.notificationService.handleError('Search failed', error);
        } finally {
            this.isSearching = false;
        }
    }

    public async openCRModal(
        node: TreeNode<AnchorNodeData>,
        event?: MenuItemCommandEvent
    ): Promise<void> {
        const target =
            this.getAnchorRowTarget(node) ??
            this.menuTarget ??
            this.getEventTargetElement(event?.originalEvent);
        this.changeRequestDetailsModal.open(node, event?.originalEvent, target);
    }

    public async openCloneSignalModal(node: TreeNode<AnchorNodeData>): Promise<void> {
        // Obtain the source data that we want to clone
        const sourceAnchor = await this.loadModalAnchorWithVersions(node);

        const sourceVersion = sourceAnchor?.validVersion;

        if (!sourceVersion) {
            throw new Error('Signal has no valid version to clone');
        }

        const sourceValues = sourceVersion as unknown as Record<string, unknown>;

        // Copy the fields in the source version to a new "clone"
        const cloneVersion = Object.fromEntries(
            this.signalModal.modalProps.versionFields
                .filter(({ field }) => field !== 'isActive')
                .map(({ field }) => [field, sourceValues[field]])
        );

        cloneVersion['name'] = `${cloneVersion['name']}_Copy`;
        cloneVersion['fullName'] = `${cloneVersion['fullName']} Copy`;

        this.currentTreeType = TreeType.Signal;
        this.signalModal.modalProps.version = cloneVersion;
        this.signalModal.modalProps.service = this.signalService;
        this.signalModal.modalProps.anchor = undefined;

        this.signalModal.modalProps.onSave = (createdItem) => {
            this.saveModal(createdItem as BaseAnchor<BaseVersion>, true);
        };

        this.signalModal.onOpen(sourceAnchor.key);
    }

    public async openModal(node: TreeNode<AnchorNodeData>): Promise<void> {
        try {
            if (await firstValueFrom(this.isOriginWorkspace$)) {
                this.pendingModalNode = node;
                await this.workspaceDialog.open();
                return;
            }
            this.currentTreeType = node.data?.anchorType;
            const modalGetter = this.modalMap[this.currentTreeType!];
            if (modalGetter) {
                const modal = modalGetter();
                if (modal && modal.modalProps) {
                    modal.modalProps.version = undefined;
                    modal.modalProps.onClose = undefined;

                    if (node.data?.anchor) {
                        const selectedCR = await firstValueFrom(
                            this.store.select((state) => state.config.changeRequest)
                        );
                        const cr0Results = selectedCR ? [] : await this.crService.getByName('CR 0');
                        const changeRequest = selectedCR ?? cr0Results[0];

                        if (!changeRequest) {
                            throw new Error('CR 0 not found');
                        }

                        modal.modalProps.version = {
                            changeRequestKey: changeRequest.key,
                        };
                    }

                    const modalAnchor = await this.loadModalAnchorWithVersions(node);

                    if (modalAnchor && node.data) {
                        node.data = { ...node.data, anchor: modalAnchor };
                    }

                    modal.modalProps.anchor = modalAnchor;
                    modal.modalProps.service = this.serviceMap[this.currentTreeType!] ?? null;
                    modal.modalProps.onSave = (createdItem) => {
                        const isNewAnchor = !modalAnchor;
                        this.saveModal(createdItem as BaseAnchor<BaseVersion>, isNewAnchor);
                    };
                    modal.onOpen?.();
                }
            }
        } catch (error: unknown) {
            this.notificationService.handleError('Failed to open modal', error);
        }
    }

    public async saveModal(
        createdAnchor?: BaseAnchor<BaseVersion>,
        isNewAnchor = false
    ): Promise<void> {
        try {
            if (isNewAnchor && createdAnchor?.key) {
                await refreshPendingChangesCount(this.store, this.pendingChangesService);

                const anchorTypeNode = this.nodes.find(
                    (node) =>
                        node.type === NodeType.anchorType &&
                        node.data?.anchorType === this.currentTreeType!
                ) as TreeNode<AnchorTypeNodeData>;

                if (anchorTypeNode) {
                    await this.addAnchorNode(anchorTypeNode, createdAnchor);
                    const newNode = findNodeByKey(anchorTypeNode.children ?? [], createdAnchor.key);
                    if (newNode) this.setCurrentAnchor(newNode);
                }
            } else {
                await this.refreshExpandedAnchors();
            }

            this.store.dispatch(refreshDynamicDiagram());
            this.cdr.markForCheck();
            this.cdr.detectChanges();
        } catch (error) {
            this.notificationService.handleError('Failed to save modal', error);
        }
    }

    private getAllocationElementDiagramName(node: TreeNode<AnchorNodeData>): string {
        return node.data?.anchor.displayName ?? node.label ?? 'Allocation Element Diagram';
    }

    private getAnchorRowTarget(node: TreeNode<AnchorNodeData>): HTMLElement | undefined {
        const key = node.data?.anchor?.key;
        if (key === undefined) return undefined;

        const target = this.elementRef.nativeElement.querySelector(`[data-anchor-key="${key}"]`);
        return target instanceof HTMLElement ? target : undefined;
    }

    private getEventTargetElement(event?: Event): HTMLElement | undefined {
        const currentTarget = event?.currentTarget;
        if (currentTarget instanceof HTMLElement) return currentTarget;

        const target = event?.target;
        return target instanceof HTMLElement ? target : undefined;
    }

    private async handleAddInstanceToTree(anchorKey: number, objectType: string): Promise<void> {
        const treeType = getTreeTypeFromDiagramLabel(objectType);
        if (!treeType) {
            this.notificationService.showError(
                `Object type '${objectType}' not supported for tree navigation`
            );
            return;
        }

        const anchorTypeNode = this.nodes.find(
            (node) => node.type === NodeType.anchorType && node.data?.anchorType === treeType
        ) as TreeNode<AnchorTypeNodeData>;

        if (!anchorTypeNode) {
            this.notificationService.showError(`Object type '${objectType}' not found in tree`);
            return;
        }

        anchorTypeNode.loading = true;
        try {
            const currentConfig = await firstValueFrom(this.store.select((state) => state.config));
            if (!currentConfig.sidebarVisible) {
                this.store.dispatch(updateSidebarVisible({ sidebarVisible: true }));
            }

            this.collapseAll();

            const freshAnchorTypeNode = this.nodes.find(
                (node) => node.type === NodeType.anchorType && node.data?.anchorType === treeType
            ) as TreeNode<AnchorTypeNodeData>;
            if (freshAnchorTypeNode) {
                freshAnchorTypeNode.loading = true;
            }

            const service = this.serviceMap[treeType];

            if (!service) {
                this.notificationService.showError(
                    `Service not available for object type '${objectType}'`
                );
                return;
            }

            const anchors = await service.getAnchorsWithValidVersion({ aKey: anchorKey });
            if (anchors.length === 0) {
                this.notificationService.showWarning(`Instance with key ${anchorKey} not found`);
                return;
            }

            freshAnchorTypeNode.expanded = true;
            await this.addAnchorNode(freshAnchorTypeNode, anchors[0]);

            const liveAnchorTypeNode = this.nodes.find(
                (node) => node.type === NodeType.anchorType && node.data?.anchorType === treeType
            ) as TreeNode<AnchorTypeNodeData>;

            const anchorNode = liveAnchorTypeNode?.children?.find(
                (child) =>
                    child.type === NodeType.anchor &&
                    (child.data as AnchorNodeData)?.anchor?.key === anchorKey
            ) as TreeNode<AnchorNodeData>;

            if (anchorNode) {
                this.setCurrentAnchor(anchorNode);
                this.instanceSelected.emit({ anchorKey, objectType });
            }
        } catch (error) {
            this.notificationService.handleError('Failed to add instance to tree', error);
        } finally {
            const nodeToUnload = this.nodes.find(
                (node) => node.type === NodeType.anchorType && node.data?.anchorType === treeType
            ) as TreeNode<AnchorTypeNodeData>;
            if (nodeToUnload) nodeToUnload.loading = false;
        }
    }

    private async loadModalAnchorWithVersions(
        node: TreeNode<AnchorNodeData>
    ): Promise<BaseAnchor<BaseVersion> | undefined> {
        const anchor = node.data?.anchor;
        const anchorType = node.data?.anchorType;

        if (!anchor || !anchorType) return undefined;

        const service = this.serviceMap[anchorType];

        if (!service) return anchor;

        const [freshAnchor] = await service.getAnchorsWithAllVersions({
            aKey: anchor.key,
            qLimit: 1,
        });

        if (!freshAnchor) return anchor;

        return {
            ...freshAnchor,
            displayName: freshAnchor.displayName ?? anchor.displayName,
        };
    }

    private async loadPendingAnchors(
        anchorTypeNode: TreeNode<AnchorTypeNodeData>
    ): Promise<BaseAnchor<BaseVersion>[]> {
        if (anchorTypeNode.parent || anchorTypeNode.data?.anchorType === TreeType.ChangeRequest) {
            return [];
        }

        const workspace = await firstValueFrom(
            this.store.select((state) => state.config.workspace)
        );
        if (!workspace?.origin) return [];

        const service = this.serviceMap[anchorTypeNode.data!.anchorType];
        if (!service) return [];

        const filter: AnchorAndVersionBaseFilter = {
            aWorkspaceKey: workspace.key,
            ...(this.searchString && { qDisplayName: this.searchString }),
        };

        const total = await service.CountAnchorsWithValidVersion(filter);
        if (total === 0) return [];

        const anchors = await service.getAnchorsWithValidVersion({
            ...filter,
            qLimit: total,
        });

        return anchors.filter((anchor) => isNewlyCreatedAnchor(anchor));
    }

    private async refreshExpandedAnchors(): Promise<void> {
        try {
            for (const node of this.nodes) {
                if (node.type === NodeType.anchorType) {
                    if (node.expanded) {
                        await this.addAnchorNode(node as TreeNode<AnchorTypeNodeData>);
                    } else {
                        node.children = undefined;
                    }
                }
            }
            this.cdr.markForCheck();
        } catch (error) {
            this.notificationService.handleError('Failed to refresh tree', error);
        }
    }
}
